// frontend/src/widgets/WorkflowTemplateListWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-15
// Updated:      2026-10-15 13:15 BST
//
// Change Log (GMT/BST):
//   2026-10-15 13:15 BST - Fixed template/version card text contrast on light backgrounds;
//                           explicit dark primary and secondary text colours.
//   2026-10-06 17:58 BST - Added explicit Archive action for non-archived workflow templates.
//   2026-10-06 15:35 BST - Corrected workflow draft provenance from unsupported "manual"
//                           to canonical "ui" source_type.
//
// Description:
//   Workflow template list and version selector widget.
//
//   Responsibilities:
//     - Search and filter workflow templates
//     - Select workflow template
//     - Select workflow version
//     - Create workflow template
//     - Create draft version from current template / version
//     - Launch governed category manager modal
//
//   Important:
//     - This widget is controlled by WorkflowPage
//     - Categories must come from workflow_template_categories, not template rows
//     - Do not derive the category dropdown from existing templates
//     - Layout is stacked vertically and relies on parent panel scroll
//     - Do not force internal section flex growth that causes overlap/clipping
//
// Mobile Responsiveness (2026-09-10):
//     - Preserves the intentionally stacked vertical architecture and parent-panel scrolling.
//     - Does NOT introduce internal section flex-growth or one-record list viewports.
//     - Search/filter/create controls remain in the same order and become touch-safe on mobile.
//     - Include Archived + Manage Categories stack cleanly on narrow screens.
//     - Template and Version cards remain the same buttons/callbacks and become width-safe touch targets.
//     - Create Draft header/action stacks cleanly on phones.
//     - Dynamic mobile sizing uses the proven Vision full-content pattern:
//         restore previous host sizing;
//         clear temporary root/content sizing;
//         measure the complete natural rendered content;
//         wait a second animation frame;
//         resize the first width-matching fixed-height mobile host.
//     - ResizeObserver watches the complete content so template/version/category count changes
//       cannot leave the mobile widget clipped.
//     - Desktop preserves the existing internally scrollable stacked panel behaviour.
//     - No duplicate mobile render tree is mounted.
//
// Operational Notes:
//     - onFiltersChange, onSelectTemplate, onSelectVersion, onCreateTemplate,
//       onCreateDraft and onCategoriesChanged semantics are unchanged.
//     - Governed workflow_template_categories remain the only category source.
//     - Category dropdowns are not derived from template rows.

import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import WorkflowTemplateCategoryManagerModal from "../components/WorkflowTemplateCategoryManagerModal.jsx";

const WorkflowTemplateListWidget = ({
  templates = [],
  versions = [],
  workflowTemplateCategories = [],
  selectedTemplateId = null,
  selectedVersionId = null,
  filters = {
    include_archived: false,
    status: "",
    category: "",
    search: "",
  },
  loading = {
    templates: false,
    versions: false,
    categories: false,
    creatingTemplate: false,
    creatingDraft: false,
    archivingTemplate: false,
  },
  onFiltersChange,
  onSelectTemplate,
  onSelectVersion,
  onCreateTemplate,
  onCreateDraft,
  onArchiveTemplate,
  onCategoriesChanged,
}) => {
  const { theme } = useTheme();

  const widgetRootRef = useRef(null);
  const contentRef = useRef(null);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const [form, setForm] = useState({
    template_key: "",
    name: "",
    description: "",
    category_id: "",
    is_system_template: false,
  });

  const visibleTemplates = useMemo(() => {
    const q = (filters.search || "").trim().toLowerCase();

    return templates.filter((t) => {
      const matchesSearch =
        !q ||
        String(t.name || "").toLowerCase().includes(q) ||
        String(t.template_key || "").toLowerCase().includes(q) ||
        String(t.category_name || t.category || "").toLowerCase().includes(q) ||
        String(t.description || "").toLowerCase().includes(q);

      return matchesSearch;
    });
  }, [templates, filters.search]);

  const categoryOptions = useMemo(() => {
    return [...workflowTemplateCategories]
      .filter((c) => !!c.is_active)
      .sort((a, b) => {
        const aSort = Number(a.sort_order || 0);
        const bSort = Number(b.sort_order || 0);
        if (aSort !== bSort) return aSort - bSort;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
  }, [workflowTemplateCategories]);

  const actionBtn = {
    background: theme.button_action?.normal || "#000",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
    width: "100%",
  };

  const generalBtn = {
    background: theme.button_general?.normal || "#222",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  };

  const inputStyle = {
    width: "100%",
    minHeight: 44,
    padding: "8px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    boxSizing: "border-box",
    fontSize: 14,
  };

  const selectStyle = {
    ...inputStyle,
    backgroundColor: "#fff",
  };

  const smallMuted = {
    fontSize: 12,
    color: "#475569",
  };

  const sectionWrap = {
    borderTop: "1px solid #e5e7eb",
    paddingTop: 12,
    flex: "0 0 auto",
  };

  const cardStyle = (selected) => ({
    color: "#1f2937",
    textAlign: "left",
    minWidth: 0,
    overflowWrap: "anywhere",
    border: selected ? `2px solid ${theme.accent_color || "#ff7a00"}` : "1px solid #e5e7eb",
    borderRadius: 10,
    background: selected ? "#fff7ed" : "#fff",
    padding: 10,
    cursor: "pointer",
    width: "100%",
  });

  const listContainerStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  };

  const statusPill = (status) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    background:
      status === "published"
        ? "#dcfce7"
        : status === "draft"
          ? "#dbeafe"
          : status === "archived" || status === "retired"
            ? "#f3f4f6"
            : "#fef3c7",
    color:
      status === "published"
        ? "#166534"
        : status === "draft"
          ? "#1d4ed8"
          : status === "archived" || status === "retired"
            ? "#4b5563"
            : "#92400e",
  });

  const handleCreateTemplate = () => {
    const payload = {
      template_key: form.template_key.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      category_id: form.category_id ? Number(form.category_id) : null,
      is_system_template: !!form.is_system_template,
    };

    if (!payload.template_key || !payload.name) {
      alert("Template key and name are required.");
      return;
    }

    onCreateTemplate?.(payload, () => {
      setForm({
        template_key: "",
        name: "",
        description: "",
        category_id: "",
        is_system_template: false,
      });
    });
  };

  const handleCreateDraft = () => {
    if (!selectedTemplateId) {
      alert("Select a template first.");
      return;
    }

    const payload = {
      version_label: null,
      change_summary: null,
      source_type: "ui",
      source_reference: null,
      clone_from_version_id: selectedVersionId || null,
    };

    onCreateDraft?.(payload);
  };

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const content = contentRef.current;

    if (!root || !content) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const naturalContentHeight = Math.ceil(
          Math.max(
            content.getBoundingClientRect().height || 0,
            content.scrollHeight || 0
          )
        );

        root.style.height = "auto";
        root.style.minHeight =
          Number.isFinite(naturalContentHeight) && naturalContentHeight > 0
            ? `${naturalContentHeight}px`
            : "0px";

        content.style.height = "auto";
        content.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    resizeObserver?.observe(content);

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";
    };
  }, [
    categoryModalOpen,
    categoryOptions.length,
    filters.category,
    filters.include_archived,
    filters.search,
    filters.status,
    loading.categories,
    loading.creatingDraft,
    loading.creatingTemplate,
    loading.templates,
    loading.versions,
    selectedTemplateId,
    selectedVersionId,
    versions.length,
    visibleTemplates.length,
  ]);

  return (
    <>
      <div
        ref={widgetRootRef}
        className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-y-auto md:overflow-x-hidden"
      >
        <style>{`
          @media (max-width: 767px) {
            .vx-workflow-template-list-content input,
            .vx-workflow-template-list-content select,
            .vx-workflow-template-list-content textarea {
              min-height: 44px;
            }

            .vx-workflow-template-list-mobile-button {
              width: 100% !important;
              min-height: 44px;
              white-space: normal !important;
            }

            .vx-workflow-template-list-card {
              min-height: 44px;
            }
          }
        `}</style>

        <div
          ref={contentRef}
          className="vx-workflow-template-list-content flex h-auto min-h-fit flex-col gap-3.5 overflow-visible pr-0 md:pr-0.5"
        >
        <div style={{ flex: "0 0 auto" }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Template Search</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
            <input
              type="text"
              value={filters.search || ""}
              onChange={(e) => onFiltersChange?.({ search: e.target.value })}
              placeholder="Search templates..."
              style={inputStyle}
            />

            <select
              value={filters.status || ""}
              onChange={(e) => onFiltersChange?.({ status: e.target.value })}
              style={selectStyle}
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="retired">Retired</option>
              <option value="archived">Archived</option>
            </select>

            <select
              value={filters.category || ""}
              onChange={(e) => onFiltersChange?.({ category: e.target.value })}
              style={selectStyle}
            >
              <option value="">All categories</option>
              {categoryOptions.map((category) => (
                <option key={category.id} value={String(category.id)}>
                  {category.name}
                </option>
              ))}
            </select>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <label className="flex min-h-[44px] items-center gap-2 text-[13px]">
                <input
                  className="h-5 w-5 md:h-auto md:w-auto"
                  type="checkbox"
                  checked={!!filters.include_archived}
                  onChange={(e) => onFiltersChange?.({ include_archived: e.target.checked })}
                />
                Include archived
              </label>

              <button
                type="button"
                className="vx-workflow-template-list-mobile-button"
                onClick={() => setCategoryModalOpen(true)}
                style={generalBtn}
              >
                Manage Categories
              </button>
            </div>
          </div>
        </div>

        <div style={sectionWrap}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Create Template</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
            <input
              type="text"
              value={form.template_key}
              onChange={(e) => setForm((p) => ({ ...p, template_key: e.target.value }))}
              placeholder="Template key"
              style={inputStyle}
            />

            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Template name"
              style={inputStyle}
            />

            <select
              value={form.category_id}
              onChange={(e) => setForm((p) => ({ ...p, category_id: e.target.value }))}
              style={selectStyle}
            >
              <option value="">No category</option>
              {categoryOptions.map((category) => (
                <option key={category.id} value={String(category.id)}>
                  {category.name}
                </option>
              ))}
            </select>

            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Description"
              style={{ ...inputStyle, minHeight: 72, resize: "vertical" }}
            />

            <label className="flex min-h-[44px] items-center gap-2 text-[13px]">
              <input
                className="h-5 w-5 md:h-auto md:w-auto"
                type="checkbox"
                checked={!!form.is_system_template}
                onChange={(e) => setForm((p) => ({ ...p, is_system_template: e.target.checked }))}
              />
              System template
            </label>

            <button
              type="button"
              className="vx-workflow-template-list-mobile-button"
              onClick={handleCreateTemplate}
              disabled={!!loading.creatingTemplate}
              style={{
                ...actionBtn,
                opacity: loading.creatingTemplate ? 0.8 : 1,
                cursor: loading.creatingTemplate ? "progress" : "pointer",
              }}
            >
              {loading.creatingTemplate ? "Creating..." : "Create Template"}
            </button>
          </div>
        </div>

        <div
          style={{
            ...sectionWrap,
            minHeight: 180,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Templates</div>

          <div style={{ ...smallMuted, marginBottom: 8 }}>
            {loading.templates ? "Loading templates..." : `${visibleTemplates.length} template(s)`}
          </div>

          <div style={listContainerStyle}>
            {visibleTemplates.map((template) => {
              const selected = String(selectedTemplateId) === String(template.id);

              return (
                <div key={template.id} style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
                <button
                  type="button"
                  className="vx-workflow-template-list-card"
                  onClick={() => onSelectTemplate?.(template)}
                  style={{ ...cardStyle(selected), flex: 1 }}
                >
                  <div className="break-words" style={{ fontWeight: 700, color: "#1f2937" }}>{template.name}</div>
                  <div className="break-all" style={{ ...smallMuted, color: "#475569" }}>{template.template_key}</div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                    <span style={statusPill(template.status)}>{template.status}</span>
                    {template.category_name ? (
                      <span style={smallMuted}>Category: {template.category_name}</span>
                    ) : template.category ? (
                      <span style={smallMuted}>Category: {template.category}</span>
                    ) : null}
                    {template.is_system_template ? <span style={smallMuted}>System</span> : null}
                  </div>
                </button>
                {template.status !== "archived" && (
                  <button
                    type="button"
                    title={`Archive ${template.name}`}
                    aria-label={`Archive ${template.name}`}
                    disabled={!!loading.archivingTemplate}
                    onClick={() => onArchiveTemplate?.(template.id)}
                    style={{ ...generalBtn, width: "auto", minWidth: 44, padding: "8px 10px" }}
                  >
                    Archive
                  </button>
                )}
                </div>
              );
            })}

            {!loading.templates && visibleTemplates.length === 0 && (
              <div style={smallMuted}>No templates found.</div>
            )}
          </div>
        </div>

        <div
          style={{
            ...sectionWrap,
            minHeight: 180,
          }}
        >
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div style={{ fontWeight: 700 }}>Versions</div>
            <button
              type="button"
              className="vx-workflow-template-list-mobile-button"
              onClick={handleCreateDraft}
              disabled={!selectedTemplateId || !!loading.creatingDraft}
              style={{
                ...generalBtn,
                opacity: !selectedTemplateId || loading.creatingDraft ? 0.7 : 1,
                cursor: !selectedTemplateId || loading.creatingDraft ? "not-allowed" : "pointer",
              }}
            >
              {loading.creatingDraft ? "Creating..." : "Create Draft"}
            </button>
          </div>

          <div style={{ ...smallMuted, marginBottom: 8 }}>
            {loading.versions
              ? "Loading versions..."
              : selectedTemplateId
                ? `${versions.length} version(s)`
                : "Select a template to load versions"}
          </div>

          <div style={listContainerStyle}>
            {versions.map((version) => {
              const selected = String(selectedVersionId) === String(version.id);

              return (
                <button
                  key={version.id}
                  type="button"
                  className="vx-workflow-template-list-card"
                  onClick={() => onSelectVersion?.(version)}
                  style={cardStyle(selected)}
                >
                  <div className="break-words" style={{ fontWeight: 700, color: "#1f2937" }}>
                    v{version.version_no}
                    {version.version_label ? ` — ${version.version_label}` : ""}
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                    <span style={statusPill(version.status)}>{version.status}</span>
                    {version.is_current ? <span style={smallMuted}>Current</span> : null}
                  </div>

                  {version.change_summary ? (
                    <div className="break-words" style={{ ...smallMuted, marginTop: 6 }}>{version.change_summary}</div>
                  ) : null}
                </button>
              );
            })}

            {!loading.versions && selectedTemplateId && versions.length === 0 && (
              <div style={smallMuted}>No versions found for this template.</div>
            )}
          </div>
        </div>
      </div>
      </div>

      <WorkflowTemplateCategoryManagerModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        onChanged={async () => {
          await onCategoriesChanged?.();
        }}
      />
    </>
  );
};

export default WorkflowTemplateListWidget;